import { useRef } from 'react';
import { Glyphicon } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import {
  FieldControl,
  FieldLabel,
} from 'components/domain/customDomains/modal/CustomDomainModal.styles';
import UploadIcon from 'components/icons/Upload.svg?react';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import Tooltip from 'components/tooltip/Tooltip';
import useNotify from 'hooks/useNotify';
import {
  FaviconControlStyled,
  FaviconPreviewStyled,
  FaviconRowStyled,
} from '../WhiteLabelSettings.styles';
import useUploadAgencyFaviconMutation from '../useUploadAgencyFaviconMutation';

const ACCEPTED_FAVICON_TYPES = [
  'image/x-icon',
  'image/vnd.microsoft.icon',
  'image/png',
  'image/svg+xml',
];

const FILE_INPUT_ID = 'favicon-input';

// Laid out like the domain section's rows so the labels share a column.
const FaviconField = ({
  faviconUrl,
  label,
  tooltip,
}: {
  faviconUrl?: string | null;
  label: string;
  tooltip: string;
}) => {
  const { t } = useTranslation();
  const { notifyError } = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadFavicon, isLoading } = useUploadAgencyFaviconMutation();

  const handleFile = (file?: File) => {
    if (!file) {
      return;
    }

    if (!ACCEPTED_FAVICON_TYPES.includes(file.type)) {
      notifyError(
        t('pageDirectBookingEngine.whiteLabelSettings.faviconInvalidFileType'),
      );
      return;
    }

    uploadFavicon(file);
  };

  const openFilePicker = () => fileInputRef.current?.click();
  const hasImage = !isLoading && !!faviconUrl;

  return (
    <FaviconRowStyled data-testid="favicon-field">
      <FieldLabel htmlFor={FILE_INPUT_ID}>
        <Tooltip id="favicon-tooltip" text={tooltip}>
          <Glyphicon data-testid="favicon-tooltip-icon" glyph="info-sign" />
        </Tooltip>
        {label}
      </FieldLabel>
      <FieldControl>
        <FaviconControlStyled>
          <FaviconPreviewStyled
            $hasImage={hasImage}
            data-testid="favicon-preview"
          >
            {isLoading && <LoadingPlaceholder width="56px" linesCount={3} />}
            {hasImage && (
              <img
                alt=""
                data-testid="favicon-preview-image"
                src={faviconUrl!}
              />
            )}
            {!isLoading && !faviconUrl && <Glyphicon glyph="picture" />}
          </FaviconPreviewStyled>

          <input
            accept={ACCEPTED_FAVICON_TYPES.join(',')}
            aria-hidden="true"
            tabIndex={-1}
            data-testid={FILE_INPUT_ID}
            id={FILE_INPUT_ID}
            onChange={(event) => {
              handleFile(event.target.files?.[0]);
              event.target.value = '';
            }}
            ref={fileInputRef}
            type="file"
          />

          <Button
            data-testid="favicon-upload-button"
            disabled={isLoading}
            leftIcon={<UploadIcon />}
            onClick={openFilePicker}
          >
            {t('common.upload')}
          </Button>
        </FaviconControlStyled>
      </FieldControl>
    </FaviconRowStyled>
  );
};

FaviconField.defaultProps = {
  faviconUrl: undefined,
};

export default FaviconField;
